import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { pickAirportBg } from "../../lib/assets";
import StoryComposer from "./StoryComposer";

/* Illinois-mockup style city guide strips for the traveler's current city.
   Two sections: "Top Experiences in {City}" (4 tiles) + "Latest Traveler Stories" (3 cards).
   All imagery uses the site's own AI-generated assets so nothing ever breaks. */

const CITY_DATA = {
  Portland: {
    experiences: [
      { id: "waterfront", name: "Waterfront Park",             blurb: "Riverside walks & festivals" },
      { id: "powells",    name: "Powell's City of Books",      blurb: "The world's largest independent bookstore" },
      { id: "roses",      name: "International Rose Test Garden", blurb: "10,000 blooms with Mt. Hood views" },
      { id: "food",       name: "Food Cart Pods",              blurb: "Portland's legendary street food" },
    ],
    stories: [
      { id: "s1", author: "Jessica L.", when: "3 days ago", title: "Weekend in Portland — food carts & falls", likes: 142, comments: 28 },
      { id: "s2", author: "Daniel K.",  when: "5 days ago", title: "Fall colours at Multnomah Falls",          likes:  96, comments: 14 },
      { id: "s3", author: "Maya R.",    when: "1 week ago", title: "A Powell's Books rabbit hole afternoon",    likes: 188, comments: 36 },
    ],
  },
};

const DEFAULT_STORIES = (city) => [
  { id: "s1", author: "Jessica L.", when: "3 days ago", title: `A perfect weekend in ${city}`, likes: 142, comments: 28 },
  { id: "s2", author: "Daniel K.",  when: "5 days ago", title: `Hidden gems around ${city}`,   likes:  96, comments: 14 },
  { id: "s3", author: "Maya R.",    when: "1 week ago", title: `Best day trips from ${city}`,  likes: 188, comments: 36 },
];

const DEFAULT_EXPERIENCES = (city) => [
  { id: "landmarks", name: "Iconic Landmarks",   blurb: `Must-see sights in ${city}` },
  { id: "food",      name: "Local Food Scene",   blurb: `Where locals eat in ${city}` },
  { id: "parks",     name: "Parks & Nature",     blurb: `Green escapes around ${city}` },
  { id: "culture",   name: "Culture & Museums",  blurb: `Stories, art and heritage of ${city}` },
];

export default function CityGuideRow({ city }) {
  const cityName = city?.name || "your city";
  const [remote, setRemote] = useState(null);
  const [userStories, setUserStories] = useState([]);

  useEffect(() => {
    api.get(`/city/${encodeURIComponent(cityName)}/summary`)
      .then((r) => setRemote(r.data || null))
      .catch(() => setRemote(null));
    api.get(`/city/${encodeURIComponent(cityName)}/stories`, { params: { limit: 3 } })
      .then((r) => setUserStories(r.data?.items || []))
      .catch(() => setUserStories([]));
  }, [cityName]);

  const rawData = CITY_DATA[cityName] || {
    experiences: remote?.experiences || DEFAULT_EXPERIENCES(cityName),
    stories: DEFAULT_STORIES(cityName),
  };
  // Prefer live experiences when the API returned some.
  const experiences = (remote?.experiences?.length ? remote.experiences : rawData.experiences)
    .map((x) => ({ ...x, img: x.img || pickAirportBg(cityName + "-" + x.id) }));
  const seedStories = rawData.stories.map((s) => ({ ...s, img: s.img || pickAirportBg(cityName + "-story-" + s.id) }));
  // User-posted stories take the first slot(s) when available.
  const stories = [
    ...userStories.map((s) => ({
      id: s.id,
      author: s.author || "Traveller",
      when: "just now",
      title: s.title,
      likes: s.likes || 0,
      comments: s.comments || 0,
      img: pickAirportBg(cityName + "-user-" + s.id),
    })),
    ...seedStories,
  ].slice(0, 3);
  const data = { experiences, stories };

  return (
    <section className="space-y-8" data-testid="home-city-guide">
      {/* Top Experiences */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-emerald-700">Local guide</div>
            <h2 className="font-serif font-800 text-2xl sm:text-3xl text-blue-900 tracking-tight mt-1">
              Top Experiences in {city?.name || "your city"}
            </h2>
          </div>
          <Link to={`/city-guides`} data-testid="home-city-guide-more"
            className="hidden sm:inline-flex items-center gap-1 text-blue-700 font-700 hover:underline text-sm">
            See all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="home-city-experiences">
          {data.experiences.map((x) => (
            <Link key={x.id} to="/city-guides" data-testid={`home-city-exp-${x.id}`}
              className="group relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all min-h-[180px]">
              <img src={x.img} alt={x.name} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/25 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-3.5 text-white">
                <div className="font-serif font-800 text-sm leading-tight drop-shadow-md">{x.name}</div>
                <div className="text-[11px] text-white/85 mt-1 leading-snug">{x.blurb}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Latest Traveler Stories */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-amber-700">From the community</div>
            <h2 className="font-serif font-800 text-2xl sm:text-3xl text-blue-900 tracking-tight mt-1">
              Latest {city?.name || "Traveler"} Stories
            </h2>
          </div>
          <Link to="/community" data-testid="home-stories-more"
            className="hidden sm:inline-flex items-center gap-1 text-blue-700 font-700 hover:underline text-sm">
            See all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid md:grid-cols-4 gap-4" data-testid="home-city-stories">
          {data.stories.map((s) => (
            <Link key={s.id} to="/community" data-testid={`home-story-${s.id}`}
              className="rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col">
              <div className="aspect-[16/9] w-full overflow-hidden bg-slate-100">
                <img src={s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-fuchsia-400 grid place-items-center text-white text-[10px] font-800">
                    {s.author.charAt(0)}
                  </span>
                  <span className="font-700 text-slate-700">{s.author}</span>
                  <span>·</span>
                  <span>{s.when}</span>
                </div>
                <div className="font-serif font-800 text-base text-blue-900 leading-tight">{s.title}</div>
                <div className="mt-auto pt-2 flex items-center gap-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-rose-500" /> {s.likes}</span>
                  <span className="inline-flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {s.comments}</span>
                </div>
              </div>
            </Link>
          ))}
          {/* Composer tile — signed-in travellers can post their story right from Home */}
          <StoryComposer city={cityName} onPosted={(p) => setUserStories((v) => [p, ...v].slice(0, 3))} />
        </div>
      </div>
    </section>
  );
}
